'use client';

/* eslint-disable no-redeclare */
import { SetStateAction, useCallback, useEffect, useState } from 'react';

import { deserialize, serialize } from './useLocalStorageState.parts';

import { safeLocalStorage } from '@/shared/utils/StorageService';

type ToPrimitive<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : never;
type ToObject<T> = T extends unknown[] | Record<string, unknown> ? T : never;

type JsonPrimitive = string | number | boolean;

type Serializable<T> = T extends JsonPrimitive ? ToPrimitive<T> : ToObject<T>;

export function useLocalStorageState<T>(
  key: string
): readonly [
  Serializable<T> | undefined,
  (value: SetStateAction<Serializable<T> | undefined>) => void,
];

export function useLocalStorageState<T>(
  key: string,
  defaultValue: Serializable<T>
): readonly [Serializable<T>, (value: SetStateAction<Serializable<T>>) => void];

export function useLocalStorageState<T>(
  key: string,
  defaultValue?: Serializable<T>
): readonly [
  Serializable<T> | undefined,
  (value: SetStateAction<Serializable<T> | undefined>) => void,
] {
  const getValue = useCallback(() => {
    const value = safeLocalStorage.get(key);

    if (value == null) {
      safeLocalStorage.set(key, serialize(defaultValue));

      return defaultValue;
    }

    try {
      const deserialized = deserialize(value);

      if (deserialized == null) {
        return defaultValue;
      }

      return deserialized as Serializable<T>;
    } catch (error) {
      return defaultValue;
    }
  }, [defaultValue, key]);

  useStorageEvent(key, () => {
    requestAnimationFrame(() => {
      setState(getValue);
    });
  });

  const [state, setState] = useState<Serializable<T> | undefined>(getValue);

  const set = useCallback(
    (value: SetStateAction<Serializable<T> | undefined>) => {
      setState((currentValue) => {
        const nextValue =
          typeof value === 'function' ? value(currentValue) : value;

        const serialized = serialize(nextValue);
        if (nextValue == null) {
          safeLocalStorage.remove(key);
        } else {
          safeLocalStorage.set(key, serialized);
        }

        window.dispatchEvent(
          new StorageEvent('storage', {
            key,
            newValue: serialized,
          })
        );

        return nextValue;
      });
    },
    [key]
  );

  return [state, set] as const;
}

function useStorageEvent(key: string, callback: () => void) {
  const memoizedHandler = useCallback(
    (event: StorageEvent) => {
      if (event.key === key) {
        callback();
      }
    },
    [key, callback]
  );

  useEffect(() => {
    window.addEventListener('storage', memoizedHandler);

    return () => {
      window.removeEventListener('storage', memoizedHandler);
    };
  }, [memoizedHandler]);
}
